import { getAuth } from 'firebase/auth'
import { GRAPHQL_URL } from '@/lib/env'

interface PresignedResult {
  url: string
  publicUrl: string
}

export async function getPresignedUploadUrl(
  bucket: string,
  filePath: string,
  contentType: string,
): Promise<PresignedResult> {
  const auth = getAuth()
  const token = await auth.currentUser?.getIdToken(false)
  if (!token) throw new Error('Not authenticated')

  const res = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      query: `query GetPreSignedS3UploadURL($bucket: String!, $filePath: String!, $contentType: String!) {
        getPreSignedS3UploadURL(bucket: $bucket, filePath: $filePath, contentType: $contentType) {
          returnCode
          message
          url
        }
      }`,
      variables: { bucket, filePath, contentType },
    }),
  })

  const json = await res.json()
  const result = json.data?.getPreSignedS3UploadURL
  if (!result || result.returnCode !== 0) {
    throw new Error(result?.message ?? 'Failed to get upload URL')
  }

  return {
    url: result.url,
    publicUrl: `https://${bucket}.s3.us-east-2.amazonaws.com/${filePath}`,
  }
}

export async function uploadFileToS3(file: File, bucket: string, filePath: string): Promise<string> {
  const { url, publicUrl } = await getPresignedUploadUrl(bucket, filePath, file.type)

  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  })

  if (!res.ok) {
    // fetch() only rejects on network-level failures, not HTTP error statuses — a bad/expired
    // presigned URL resolves here as a normal (non-ok) response, not a thrown error, so without
    // this check a failed S3 write silently reports success with a URL nothing was written to.
    const body = await res.text().catch(() => '')
    throw new Error(`S3 upload failed (${res.status}): ${body || res.statusText}`)
  }

  return publicUrl
}

export function generateUploadPath(prefix: string, teamId: string, filename: string): string {
  const ext = filename.split('.').pop()
  const suffix = Math.random().toString(36).substring(2, 8)
  return `${prefix}/${teamId}/${suffix}.${ext}`
}
